import type { SelectorObj } from '../../shared.types'
import { getInflatedDynamicSlotIdSelector, getRegularIdSelector } from '../../utils/selectorsUtils'
import { getVariantSelectors } from '../../utils/variantsUtils'
import type { DomApplier } from '../cssFeatures.types'
import type { Selectors } from '../cssFeature.types'
import type { BreakpointsToStickyTopOffsetMap, StickyTopOffset } from './responsiveLayout.types'
import { LANDING_PAGE_TOP_OFFSET } from './stickyTopOffsetCompNode'

const PINNED_LAYER_SUFFIX: string = '-pinned-layer'
const SITE_CONTAINER_WITHOUT_HEADER_SELECTOR = '#masterPage.landingPage'
const SITE_CONTAINER_WITH_HEADER_SELECTOR = '#masterPage:not(.landingPage)'

const isSelectorIdSelector = (selector: string, compId: string, isDynamicSlotsExperimentOn: boolean): boolean =>
	selector === getRegularIdSelector(compId) ||
	(isDynamicSlotsExperimentOn && selector === getInflatedDynamicSlotIdSelector(compId))

const addLayoutSelectorType = (
	compId: string,
	selector: string,
	layoutSelectorType: string = '',
	shouldOmitWrapperLayers: boolean = false,
	isDynamicSlotsExperimentOn: boolean = false
): string => {
	switch (layoutSelectorType) {
		case '::before':
			return `${selector}::before`
		case 'next-siblings':
			return `${selector} ~ *`
		case 'component-one-cell-grid':
			return `${selector}:not(.${compId}-container)`
		case 'children':
			return `${selector} > *`
		case 'component':
		case 'item':
			return selector
		default:
			if (shouldOmitWrapperLayers) {
				if (isSelectorIdSelector(selector, compId, isDynamicSlotsExperimentOn)) {
					return `.${compId}-${layoutSelectorType}`
				}
			} else {
				return `${selector} .${compId}-${layoutSelectorType}`
			}
			return selector
	}
}

const responsiveLayoutDomSelector = (
	compId: string,
	idSelector: string,
	variantSelectors: Selectors,
	selector: string,
	shouldOmitWrapperLayers: boolean,
	isDynamicSlotsExperimentOn: boolean
): string =>
	variantSelectors
		.map((variantSelector) =>
			addLayoutSelectorType(
				compId,
				variantSelector,
				selector,
				shouldOmitWrapperLayers,
				isDynamicSlotsExperimentOn
			)
		)
		.join(',')

const pinnedLayerDomSelector = (idSelector: string): string => `${idSelector}${PINNED_LAYER_SUFFIX}`

const isStickyTopOffsetMap = (stickyTopOffset: StickyTopOffset): stickyTopOffset is BreakpointsToStickyTopOffsetMap =>
	!!stickyTopOffset && typeof stickyTopOffset !== 'boolean'

export const responsiveLayoutDomApplier: DomApplier<'responsiveLayout'> = (
	{ id: compId },
	idSelector,
	breakpointId,
	{ responsiveLayout, pinnedLayer, layoutVariants, stickyTopOffset },
	accumulatedData
) => {
	if (!responsiveLayout && !pinnedLayer && !isStickyTopOffsetMap(stickyTopOffset)) {
		return null
	}

	const { isDynamicSlotsExperimentOn = false, shouldOmitWrapperLayers = false } = responsiveLayout || {}
	const acc: SelectorObj = {}
	const responsiveLayoutInBreakpoint = responsiveLayout?.css[breakpointId]
	for (const variantKey in responsiveLayoutInBreakpoint) {
		const variantSelectors = getVariantSelectors(variantKey, layoutVariants, compId, accumulatedData)
		const selectorObj = responsiveLayoutInBreakpoint[variantKey]
		for (const selector in selectorObj) {
			const domSelector = responsiveLayoutDomSelector(
				compId,
				idSelector,
				variantSelectors,
				selector,
				shouldOmitWrapperLayers,
				isDynamicSlotsExperimentOn
			)
			acc[domSelector] = Object.assign(acc[domSelector] || {}, selectorObj[selector])
		}
	}

	const pinnedLayerInBreakpoint = pinnedLayer?.[breakpointId]
	for (const variantKey in pinnedLayerInBreakpoint) {
		const selectorObj = pinnedLayerInBreakpoint[variantKey]
		const domSelector = pinnedLayerDomSelector(idSelector)
		for (const selector in selectorObj) {
			acc[domSelector] = selectorObj[selector]
		}
	}

	if (isStickyTopOffsetMap(stickyTopOffset) && stickyTopOffset[breakpointId]) {
		acc[SITE_CONTAINER_WITH_HEADER_SELECTOR] = stickyTopOffset[breakpointId]
		acc[SITE_CONTAINER_WITHOUT_HEADER_SELECTOR] = LANDING_PAGE_TOP_OFFSET
	}

	return acc
}
